import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { socketService } from '../../../services/socket.service';
import { notify } from '@core/notifications/notify';
import AIAdvisorPanel from '../../../components/AIAdvisorPanel';
import CatchMeUpPanel from '../../../components/CatchMeUpPanel';
import {
  Activity,
  BarChart3,
  Server,
  Settings,
  TrendingUp,
  Sparkles,
  MessageSquare,
} from 'lucide-react';
import {
  CommandBar,
  OverviewTab,
  SystemHealthTab,
  ActivityTab,
  AnalyticsTab,
  ManagementTab,
} from '../components/dashboard';
import type {
  DashboardStats,
  CompanyStatusStats,
  FrameworkUsageStats,
  TabId,
  TabConfig,
} from '../components/dashboard';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [companyStats, setCompanyStats] = useState<CompanyStatusStats | null>(null);
  const [frameworkUsage, setFrameworkUsage] = useState<FrameworkUsageStats | null>(null);
  const [complianceStats, setComplianceStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  // Tab management
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Analytics visibility toggle
  const [showAnalytics, setShowAnalytics] = useState(true);

  // Intelligent dashboard features
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditLogsTotal, setAuditLogsTotal] = useState<number>(0);
  const [criticalAlerts, setCriticalAlerts] = useState<any[]>([]);
  const [pendingTasks, setPendingTasks] = useState<any[]>([]);
  const [systemHealth, setSystemHealth] = useState({
    overdueTasks: 0,
    pendingReviews: 0,
    activeAlerts: 0
  });

  // Real data states
  const [ganttTasks, setGanttTasks] = useState<any[]>([]);
  const [systemHealthData, setSystemHealthData] = useState<any>({
    overall: 0,
    categories: [],
    trend: [],
    labels: []
  });
  const [systemMetrics, setSystemMetrics] = useState<any>(null);
  const [healthChecks, setHealthChecks] = useState<any[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);

  // AI System Advisor state
  const [systemAdvisor, setSystemAdvisor] = useState<{
    recommendations: any[];
    priority: 'low' | 'medium' | 'high' | 'critical';
    summary: string;
    nextActions: any[];
  } | null>(null);
  const [advisorLoading, setAdvisorLoading] = useState(false);

  // AI Advisor Panel state
  const [aiAdvisorPanelOpen, setAiAdvisorPanelOpen] = useState(false);

  // Catch Me Up panel state (the digest panel that replaces the old advisor)
  const [catchMeUpOpen, setCatchMeUpOpen] = useState(false);

  // Demo mode toggle
  const [demoModeEnabled, setDemoModeEnabled] = useState(false);
  const [demoModeLoading, setDemoModeLoading] = useState(false);

  // Rate limiting toggle
  const [rateLimitDisabled, setRateLimitDisabled] = useState(false);
  const [rateLimitLoading, setRateLimitLoading] = useState(false);

  // Demo framework generator modal
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  // Real data states for charts
  const [platformRiskData, setPlatformRiskData] = useState<any[]>([]);
  const [activityFeedData, setActivityFeedData] = useState<any[]>([]);
  const [timelineActivityData, setTimelineActivityData] = useState<any[]>([]);
  const [frameworkPerformanceData, setFrameworkPerformanceData] = useState<any[]>([]);
  const [emissionsData, setEmissionsData] = useState<any>({
    labels: [],
    scope1: [],
    scope2: [],
    scope3: [],
    total: []
  });
  const [userEngagementData, setUserEngagementData] = useState<any>({
    labels: [],
    activeUsers: [],
    newUsers: [],
    sessions: []
  });
  const [systemPerformanceData, setSystemPerformanceData] = useState<any>({
    labels: [],
    responseTime: [],
    throughput: [],
    errorRate: []
  });
  const [complianceDistributionData, setComplianceDistributionData] = useState<any>({
    labels: [],
    values: []
  });
  const [platformMetrics, setPlatformMetrics] = useState<any>({
    overallCompliance: 0,
    frameworksCompleted: 0,
    totalFrameworks: 0,
    riskScore: 0,
    auditScore: 0,
    trendDirection: 'up',
    trendPercentage: 0,
    frameworks: [],
    complianceTrend: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      compliance: [0, 0, 0, 0, 0, 0],
      target: [90, 90, 90, 90, 90, 90],
      risk: [10, 10, 10, 10, 10, 10]
    }
  });

  // Analytics calculations
  const analytics = useMemo(() => {
    const totalCompanies = stats?.totalCompanies || 0;
    const totalUsers = stats?.totalUsers || 0;
    const totalFrameworks = stats?.totalFrameworks || 0;
    const totalAuditLogs = auditLogsTotal || auditLogs.length;
    const criticalAlertsCount = criticalAlerts.length;
    const pendingTasksCount = pendingTasks.length;
    const systemHealthScore = systemHealthData.overall || 0;
    const activeCompanies = companyStats?.active || 0;
    const inactiveCompanies = companyStats?.inactive || 0;
    const expiringCompanies = companyStats?.expiring || 0;

    const userGrowth: number | null = null;
    const companyGrowth: number | null = null;
    const frameworkGrowth: number | null = null;

    const avgResponseTime = systemMetrics?.apiResponseTime || null;
    const errorRate = systemMetrics?.errorRate != null ? systemMetrics.errorRate * 100 : null;
    const uptime = systemHealthData?.uptime || null;

    return {
      totalCompanies,
      totalUsers,
      totalFrameworks,
      totalAuditLogs,
      criticalAlertsCount,
      pendingTasksCount,
      systemHealthScore,
      activeCompanies,
      inactiveCompanies,
      expiringCompanies,
      userGrowth,
      companyGrowth,
      frameworkGrowth,
      avgResponseTime,
      errorRate,
      uptime
    };
  }, [stats, auditLogs, auditLogsTotal, criticalAlerts, pendingTasks, systemHealthData, companyStats, systemMetrics]);

  // Format uptime from seconds — show the largest meaningful unit only.
  //   < 1 min  → "42s"
  //   < 1 hr   → "54m"
  //   < 1 day  → "3h 12m"   (drop the minutes when they are zero)
  //   >= 1 day → "2d 4h"    (drop the hours when they are zero)
  const formatUptime = (seconds: number | null) => {
    if (seconds === null || seconds === undefined) return '\u2014';
    if (seconds < 60) return `${Math.floor(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    if (hours < 24) return remMinutes ? `${hours}h ${remMinutes}m` : `${hours}h`;
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    return remHours ? `${days}d ${remHours}h` : `${days}d`;
  };

  // Safe growth formatter
  const formatGrowth = (v: number | null) => v !== null ? `+${v}%` : '\u2014';

  // Dynamic welcome message based on system state
  const getWelcomeMessage = useMemo(() => {
    const sysHealth = systemHealthData.overall || 0;
    const avgResponseTime = analytics.avgResponseTime || 0;
    const errRate = analytics.errorRate || 0;
    const alertsCount = analytics.criticalAlertsCount || 0;

    const hasCriticalIssues = alertsCount > 0 || sysHealth < 70 || errRate > 5 || avgResponseTime > 2000;
    const hasMinorIssues = sysHealth < 90 || errRate > 1 || avgResponseTime > 1000;
    const isExcellent = sysHealth >= 95 && errRate < 0.5 && avgResponseTime < 500 && alertsCount === 0;

    const getActions = () => {
      const actions: { text: string; onClick: () => void }[] = [];
      if (alertsCount > 0) {
        actions.push({ text: "Review Critical Alerts", onClick: () => navigate('/admin/audit-logs') });
      }
      if (sysHealth < 90) {
        actions.push({ text: "Check System Health", onClick: () => setActiveTab("system-health") });
      }
      if (avgResponseTime !== null && avgResponseTime > 1000) {
        actions.push({ text: "Monitor Performance", onClick: () => setActiveTab("system-health") });
      }
      if (errRate !== null && errRate > 1) {
        actions.push({ text: "Review System Logs", onClick: () => navigate('/admin/system-logs') });
      }
      return actions.slice(0, 2);
    };

    if (hasCriticalIssues) {
      return {
        greeting: "Attention needed, Admin",
        message: "Your compliance platform requires immediate attention",
        status: "critical",
        actions: getActions()
      };
    } else if (hasMinorIssues) {
      return {
        greeting: "Welcome back, Admin",
        message: "Your compliance platform is operational but needs monitoring",
        status: "warning",
        actions: getActions()
      };
    } else if (isExcellent) {
      return {
        greeting: "Welcome back, Admin",
        message: "Your compliance platform is performing excellently",
        status: "excellent",
        actions: []
      };
    } else {
      return {
        greeting: "Welcome back, Admin",
        message: "Your compliance platform is running smoothly",
        status: "good",
        actions: []
      };
    }
  }, [systemHealthData.overall, analytics.avgResponseTime, analytics.errorRate, analytics.criticalAlertsCount, analytics.uptime]);

  // Function to trigger AI system check with real data
  const triggerSystemCheck = async () => {
    setAdvisorLoading(true);
    try {
      console.log('Collecting comprehensive system data for AI analysis...');

      setSystemAdvisor({
        recommendations: [],
        priority: 'low',
        summary: '\ud83d\udcca Collecting system data... Gathering audit logs, health checks, performance metrics, and security alerts.',
        nextActions: []
      });

      const [auditData, healthData, metricsData, alertsData, userData, companyData, complianceStatsData, companiesList] = await Promise.all([
        fetch('/api/admin/audit-logs?limit=100', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.ok ? res.json() : { data: [] }).catch(() => ({ data: [] })),
        fetch('/api/health-check/system/health', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.ok ? res.json() : null).catch(() => null),
        fetch('/api/monitoring/metrics/summary', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.ok ? res.json() : null).catch(() => null),
        fetch('/api/monitoring/alerts?limit=50', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.ok ? res.json() : { data: [] }).catch(() => ({ data: [] })),
        fetch('/api/admin/dashboard/stats', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.ok ? res.json() : { data: {} }).catch(() => ({ data: {} })),
        fetch('/api/admin/dashboard/companies/status', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.ok ? res.json() : {}).catch(() => ({})),
        fetch('/api/compliance/journey/stats', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.ok ? res.json() : null).catch(() => null),
        fetch('/api/companies', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(res => res.ok ? res.json() : { data: [] }).catch(() => ({ data: [] }))
      ]);

      console.log('System data collected:', {
        auditLogs: auditData.data?.length || 0,
        hasHealthData: !!healthData,
        hasMetrics: !!metricsData,
        alerts: alertsData.data?.length || 0,
        hasUserData: !!userData.data,
        hasCompanyData: Object.keys(companyData).length > 0
      });

      const hasRealData = (auditData.data && auditData.data.length > 0) ||
                         healthData?.data ||
                         metricsData?.data ||
                         (alertsData.data && alertsData.data.length > 0);

      if (!hasRealData) {
        setSystemAdvisor({
          recommendations: [],
          priority: 'medium',
          summary: 'AI analysis complete but limited data available. Some system monitoring APIs may not be responding.',
          nextActions: [{
            text: 'Check System Health',
            onClick: () => setActiveTab('system-health'),
            priority: 'medium'
          }]
        });
        setAdvisorLoading(false);
        return;
      }

      setSystemAdvisor({
        recommendations: [],
        priority: 'low',
        summary: '\ud83e\udde0 Preparing comprehensive system analysis... Sending data to AI for intelligent evaluation.',
        nextActions: []
      });

      const systemData = {
        auditLogs: auditData.data || [],
        healthData,
        metricsData,
        alertsData: alertsData.data || [],
        currentStats: stats,
        currentAnalytics: analytics,
        userData: userData.data || {},
        companyData,
        complianceData: complianceStatsData?.data || {},
        companiesList: companiesList?.data || [],
        timestamp: new Date().toISOString(),
        systemOverview: {
          totalUsers: stats?.totalUsers || 0,
          totalCompanies: stats?.totalCompanies || 0,
          totalFrameworks: stats?.totalFrameworks || 0,
          activeFrameworks: stats?.activeFrameworks || 0,
          pendingAssignments: stats?.pendingAssignments || 0,
          completedAssignments: stats?.completedAssignments || 0,
          expiringSoon: stats?.expiringSoon || 0
        }
      };

      console.log('Prepared comprehensive system data for AI analysis');

      const localAnalysis = analyzeRealSystemData(systemData);
      setSystemAdvisor(localAnalysis);
    } catch (error) {
      console.error('AI System Check failed:', error);
      setSystemAdvisor({
        recommendations: [],
        priority: 'medium',
        summary: 'AI analysis encountered an error. Please check your API configuration and try again.',
        nextActions: [{
          text: 'Check AI Settings',
          onClick: () => navigate('/admin/ai-settings'),
          priority: 'medium'
        }]
      });
    } finally {
      setAdvisorLoading(false);
    }
  };

  // Analyze real system data with AI-like intelligence
  const analyzeRealSystemData = (data: any) => {
    const { auditLogs: logs, healthData, metricsData, alertsData, currentStats } = data;

    const issues: string[] = [];
    const recommendations: any[] = [];
    const nextActions: any[] = [];

    if (healthData?.data?.summary) {
      const healthScore = healthData.data.summary.score || 0;
      if (healthScore < 70) {
        issues.push('Critical system health issues detected');
        recommendations.push({
          type: 'critical', title: 'System Health Critical',
          description: `System health score: ${healthScore}% - Multiple components showing degradation`,
          action: 'Immediate investigation required', link: '/admin/system-health', priority: 1
        });
        nextActions.push({ text: 'Investigate System Health', onClick: () => setActiveTab('system-health'), priority: 'critical' });
      }
    }

    if (metricsData?.data) {
      const apiResponseTime = metricsData.data.api?.responseTime || 0;
      const errRate = metricsData.data.errors?.rate || 0;
      const cpuUsage = metricsData.data.cpu?.usage || 0;

      if (apiResponseTime > 2000) {
        issues.push(`API response time critically high: ${apiResponseTime}ms`);
        recommendations.push({
          type: 'critical', title: 'API Performance Critical',
          description: `Average API response time: ${apiResponseTime}ms - Significantly above acceptable limits`,
          action: 'Optimize API performance immediately', link: '/admin/system-health', priority: 1
        });
        nextActions.push({ text: 'Optimize API Performance', onClick: () => setActiveTab('system-health'), priority: 'critical' });
      }

      if (errRate !== null && errRate > 5) {
        issues.push(`Critical error rate: ${errRate.toFixed(2)}%`);
        recommendations.push({
          type: 'critical', title: 'High Error Rate',
          description: `${errRate.toFixed(2)}% error rate detected - System stability compromised`,
          action: 'Investigate and resolve errors immediately', link: '/admin/system-logs', priority: 1
        });
        nextActions.push({ text: 'Review System Logs', onClick: () => navigate('/admin/system-logs'), priority: 'critical' });
      }

      if (cpuUsage > 90) {
        issues.push(`Critical CPU usage: ${cpuUsage}%`);
        recommendations.push({
          type: 'critical', title: 'CPU Usage Critical',
          description: `CPU usage at ${cpuUsage}% - System performance severely impacted`,
          action: 'Reduce load or scale resources', link: '/admin/system-health', priority: 2
        });
      }
    }

    if (alertsData.length > 0) {
      const critAlerts = alertsData.filter((alert: any) => alert.severity === 'critical' || alert.severity === 'high');
      if (critAlerts.length > 0) {
        issues.push(`${critAlerts.length} critical alerts active`);
        recommendations.push({
          type: 'critical', title: 'Critical Alerts Active',
          description: `${critAlerts.length} critical/high priority alerts require immediate attention`,
          action: 'Review and resolve critical alerts', link: '/admin/audit-logs', priority: 1
        });
        nextActions.push({ text: 'Address Critical Alerts', onClick: () => navigate('/admin/audit-logs'), priority: 'critical' });
      }
    }

    if (logs.length > 0) {
      const recentLogs = logs.slice(0, 20);
      const errorLogs = recentLogs.filter((log: any) => log.action?.includes('error') || log.action?.includes('fail'));
      const securityLogs = recentLogs.filter((log: any) => log.action?.includes('security') || log.action?.includes('auth'));

      if (errorLogs.length > recentLogs.length * 0.3) {
        issues.push('High frequency of error events in audit logs');
        recommendations.push({
          type: 'warning', title: 'Frequent Error Events',
          description: `${errorLogs.length} error events in recent audit logs - Investigate patterns`,
          action: 'Review error patterns and resolve root causes', link: '/admin/audit-logs', priority: 2
        });
      }

      if (securityLogs.length > 5) {
        issues.push('Elevated security-related activity');
        recommendations.push({
          type: 'warning', title: 'Security Activity',
          description: `${securityLogs.length} security events detected - Monitor for threats`,
          action: 'Review security logs and enhance monitoring', link: '/admin/audit-logs', priority: 2
        });
      }
    }

    if (!currentStats?.totalCompanies || currentStats.totalCompanies === 0) {
      issues.push('No companies configured in system');
      recommendations.push({
        type: 'warning', title: 'Company Setup Required',
        description: 'System has no companies configured - Basic setup incomplete',
        action: 'Complete initial company onboarding', link: '/admin/companies', priority: 2
      });
      nextActions.push({ text: 'Setup Companies', onClick: () => navigate('/admin/companies'), priority: 'high' });
    }

    if (!currentStats?.totalUsers || currentStats.totalUsers === 0) {
      issues.push('No users configured in system');
      recommendations.push({
        type: 'warning', title: 'User Setup Required',
        description: 'System has no users configured - Access management needed',
        action: 'Create initial user accounts and roles', link: '/admin/users', priority: 2
      });
      nextActions.push({ text: 'Create User Accounts', onClick: () => navigate('/admin/users'), priority: 'high' });
    }

    let priority: 'low' | 'medium' | 'high' | 'critical' = 'low';
    if (recommendations.some(r => r.priority === 1)) priority = 'critical';
    else if (recommendations.some(r => r.priority === 2)) priority = 'high';
    else if (recommendations.length > 0) priority = 'medium';

    let summary: string;
    if (issues.length === 0) {
      summary = 'System analysis complete: All systems operating within normal parameters.';
    } else {
      const criticalCount = recommendations.filter(r => r.type === 'critical').length;
      const warningCount = recommendations.filter(r => r.type === 'warning').length;
      summary = `System analysis complete: Found ${issues.length} issue${issues.length === 1 ? '' : 's'} requiring attention. `;
      if (criticalCount > 0) summary += `${criticalCount} critical issue${criticalCount === 1 ? '' : 's'} detected. `;
      if (warningCount > 0) summary += `${warningCount} warning${warningCount === 1 ? '' : 's'} detected. `;
      summary += `Top issues: ${issues.slice(0, 2).join(', ')}${issues.length > 2 ? '...' : ''}`;
    }

    return {
      recommendations: recommendations.sort((a, b) => a.priority - b.priority),
      priority,
      summary,
      nextActions: nextActions.slice(0, 3)
    };
  };

  // Fetch demo mode status
  const fetchDemoMode = async () => {
    try {
      const res = await fetch('/api/auth/demo-accounts');
      const data = await res.json();
      setDemoModeEnabled(data?.data?.enabled || false);
    } catch { /* ignore */ }
  };

  // Fetch rate limit status
  const fetchRateLimitStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/settings/system/all', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      const configs = data?.data || data || [];
      const rlConfig = (Array.isArray(configs) ? configs : []).find((c: any) => c.config_key === 'rate_limiting_disabled');
      setRateLimitDisabled(rlConfig?.config_value === '1');
    } catch { /* ignore */ }
  };

  // Toggle rate limiting
  const toggleRateLimit = async () => {
    setRateLimitLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/settings/system/rate_limiting_disabled', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-csrf-token': document.cookie.split('csrf_token=')[1]?.split(';')[0] || ''
        },
        body: JSON.stringify({ value: !rateLimitDisabled, type: 'boolean' })
      });
      setRateLimitDisabled(!rateLimitDisabled);
      notify.success(rateLimitDisabled ? 'Rate limiting re-enabled' : 'Rate limiting disabled for testing');
    } catch {
      notify.error('Failed to toggle rate limiting');
    } finally {
      setRateLimitLoading(false);
    }
  };

  // Toggle demo mode
  const toggleDemoMode = async () => {
    setDemoModeLoading(true);
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/settings/system/demo_mode_enabled', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-csrf-token': document.cookie.split('csrf_token=')[1]?.split(';')[0] || ''
        },
        body: JSON.stringify({ value: !demoModeEnabled, type: 'boolean' })
      });
      setDemoModeEnabled(!demoModeEnabled);
      notify.success(demoModeEnabled ? 'Demo mode disabled' : 'Demo mode enabled \u2014 demo accounts now visible on login page');
    } catch {
      notify.error('Failed to toggle demo mode');
    } finally {
      setDemoModeLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchDemoMode();
    fetchRateLimitStatus();

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.id) {
      socketService.connect(user.id);
    }

    const handleNotification = (event: CustomEvent) => {
      const notification = event.detail;
      notify.info(notification.message || 'New notification received');
      if (notification.type === 'health-check' || notification.type === 'alert') {
        setRecentAlerts(prev => [
          {
            id: notification.id,
            type: notification.type,
            message: notification.message,
            severity: notification.severity || 'warning',
            timestamp: notification.timestamp
          },
          ...prev
        ].slice(0, 50));
      }
    };

    const handleHealthCheckUpdate = (event: CustomEvent) => {
      const data = event.detail;
      console.log('Health check update:', data);
      if (data.overallHealth !== undefined) {
        setSystemHealthData((prev: any) => ({
          ...prev,
          overall: data.overallHealth,
          trend: data.trend || prev.trend
        }));
      }
      if (data.components) {
        setHealthChecks(data.components);
      }
    };

    const handleSystemMetricsUpdate = (event: CustomEvent) => {
      const data = event.detail;
      console.log('System metrics update:', data);
      setSystemMetrics((prev: any) => ({
        ...prev,
        ...data
      }));
    };

    window.addEventListener('notificationReceived', handleNotification as EventListener);
    window.addEventListener('healthCheckUpdate', handleHealthCheckUpdate as EventListener);
    window.addEventListener('systemMetricsUpdate', handleSystemMetricsUpdate as EventListener);

    return () => {
      window.removeEventListener('notificationReceived', handleNotification as EventListener);
      window.removeEventListener('healthCheckUpdate', handleHealthCheckUpdate as EventListener);
      window.removeEventListener('systemMetricsUpdate', handleSystemMetricsUpdate as EventListener);
    };
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const fetchWithFallback = async (url: string, fallback: any = {}) => {
        try {
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          return data.data || fallback;
        } catch (error) {
          console.warn(`Failed to fetch ${url}:`, error);
          return fallback;
        }
      };

      const [statsData, companyStatsData, frameworkUsageData] = await Promise.all([
        fetchWithFallback('/api/admin/dashboard/stats', {
          totalCompanies: 0, totalUsers: 0, totalFrameworks: 0, activeFrameworks: 0,
          totalAssignments: 0, pendingAssignments: 0, completedAssignments: 0, expiringSoon: 0, recentActivity: []
        }),
        fetchWithFallback('/api/admin/dashboard/companies/status', { active: 0, inactive: 0, expiring: 0 }),
        fetchWithFallback('/api/admin/dashboard/frameworks/usage', { mostUsed: [], leastUsed: [], recentlyAssigned: [] })
      ]);

      setStats(statsData);
      setCompanyStats(companyStatsData);
      setFrameworkUsage(frameworkUsageData);
      setComplianceStats(complianceStats);

      if (statsData.data) {
        setSystemHealth({
          overdueTasks: statsData.data.expiringSoon || 0,
          pendingReviews: statsData.data.pendingAssignments || 0,
          activeAlerts: statsData.data.totalAssignments || 0
        });
      }

      // Fetch tab-specific data in background (non-blocking)
      fetchIntelligentData();
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchIntelligentData = async () => {
    try {
      // Fetch audit logs
      try {
        const auditRes = await fetch('/api/admin/audit-logs?limit=100', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (auditRes.ok) {
          const auditData = await auditRes.json();
          setAuditLogs(auditData.data || []);
          setAuditLogsTotal(auditData.pagination?.total || auditData.data?.length || 0);
        }
      } catch (auditError) {
        console.warn('Audit logs not available:', auditError);
        setAuditLogs([]);
        setAuditLogsTotal(0);
      }

      // Fetch real system health data
      try {
        const healthRes = await fetch('/api/health-check/system/health', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (healthRes.ok) {
          const healthData = await healthRes.json();
          if (healthData.success && healthData.data) {
            setHealthChecks(healthData.data.components || []);
            const overallHealth = healthData.data.summary?.score || 85;
            const categories = healthData.data.components?.map((comp: any) => ({
              name: comp.name || comp.component || 'Unknown',
              score: comp.status === 'healthy' ? 100 : comp.status === 'degraded' ? 60 : 30,
              target: 95, weight: 20
            })) || [];
            setSystemHealthData({
              overall: overallHealth, categories,
              trend: [overallHealth - 5, overallHealth - 3, overallHealth - 1, overallHealth, overallHealth + 1, overallHealth],
              labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
            });
          }
        }

        const infoRes = await fetch('/api/admin/system-health/info', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (infoRes.ok) {
          const infoData = await infoRes.json();
          if (infoData.success && infoData.data) {
            const uptimeStr = infoData.data.processUptime || infoData.data.uptime || '';
            const match = typeof uptimeStr === 'string' ? uptimeStr.match(/([\d.]+)/) : null;
            const uptimeSeconds = match ? parseFloat(match[1]) * 3600 : null;
            setSystemHealthData((prev: any) => ({ ...prev, uptime: uptimeSeconds }));
          }
        }
      } catch (healthError) {
        console.warn('Health checks not available:', healthError);
        setHealthChecks([]);
      }

      // Fetch system metrics
      try {
        const metricsRes = await fetch('/api/admin/system-health', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (metricsRes.ok) {
          const metricsData = await metricsRes.json();
          if (metricsData.success && metricsData.data?.checks) {
            const checks = metricsData.data.checks;
            setSystemMetrics({
              apiResponseTime: checks.database?.responseTime ?? null,
              databaseConnections: checks.database?.connected ? 1 : 0,
              errorRate: null,
              cpuUsage: checks.cpu?.usagePercent ?? null,
              memoryUsage: checks.memory?.percentUsed ?? null,
              storageUsage: null,
            });
            setSystemHealthData((prev: any) => ({
              ...prev,
              uptime: prev.uptime,
              overall: metricsData.data.healthScore ?? prev.overall,
            }));
          }
        }

        // Uptime already fetched from first system-health/info call above
      } catch (metricsError) {
        console.warn('System health metrics not available:', metricsError);
        setSystemMetrics(null);
      }

      // Fetch alerts
      try {
        const alertsRes = await fetch('/api/monitoring/alerts?limit=100', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (alertsRes.ok) {
          const alertsData = await alertsRes.json();
          if (alertsData.success && alertsData.data) {
            setRecentAlerts(alertsData.data.map((alert: any) => ({
              id: alert.id,
              type: alert.type || 'system',
              message: alert.message || alert.title,
              severity: alert.severity || 'warning',
              timestamp: alert.timestamp || alert.created_at
            })));
          }
        }
      } catch (alertsError) {
        console.warn('Monitoring alerts not available:', alertsError);
        setRecentAlerts(auditLogs.slice(0, 5).filter(log => log.action?.includes('error') || log.action?.includes('alert')).map(log => ({
          id: log.id, type: 'security',
          message: log.details || log.action,
          severity: 'warning', timestamp: log.created_at
        })));
      }

      // Use audit logs as alerts
      setRecentAlerts(auditLogs.slice(0, 5).filter(log => log.action?.includes('error') || log.action?.includes('alert')).map(log => {
        let message = log.action || 'System alert';
        if (log.details) {
          if (typeof log.details === 'string') {
            message = log.details;
          } else if (typeof log.details === 'object') {
            if (log.details.email && log.details.loginTime) message = `User ${log.details.email} login detected`;
            else if (log.details.login_method) message = `Login via ${log.details.login_method}`;
            else message = log.action || 'System alert';
          }
        }
        return { id: log.id, type: 'security', message, severity: 'warning', timestamp: log.created_at };
      }));

      // Generate critical alerts from logs
      const criticalFromLogs = auditLogs.filter(log =>
        log.action?.includes('error') || log.action?.includes('security') || log.action?.includes('breach')
      ).slice(0, 4).map(log => {
        let message = 'System alert detected';
        if (log.details) {
          if (typeof log.details === 'string') message = log.details;
          else if (typeof log.details === 'object') {
            if (log.details.email && log.details.loginTime) message = `Security alert: User ${log.details.email} login activity`;
            else if (log.details.login_method) message = `Security alert: Login attempt via ${log.details.login_method}`;
            else message = `Security alert: ${log.action || 'Unknown activity'}`;
          }
        }
        return { id: log.id, title: log.action || 'Critical Alert', message, type: 'security', severity: 'high', created_at: log.created_at };
      });
      setCriticalAlerts(criticalFromLogs);

      // Pending tasks
      const mockTasks: any[] = [];
      if (stats?.expiringSoon && stats.expiringSoon > 0) {
        mockTasks.push({
          id: '1', title: 'Review Expiring Framework Assignments',
          description: `${stats.expiringSoon} framework assignments are expiring soon`,
          priority: 'high', due_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), status: 'pending'
        });
      }
      if (companyStats?.inactive && companyStats.inactive > 0) {
        mockTasks.push({
          id: '2', title: 'Activate Inactive Companies',
          description: `${companyStats.inactive} companies need activation`,
          priority: 'medium', due_date: null, status: 'pending'
        });
      }
      setPendingTasks(mockTasks);

      // Create Gantt chart data
      if (frameworkUsage?.recentlyAssigned && frameworkUsage.recentlyAssigned.length > 0) {
        const ganttTasksData = frameworkUsage.recentlyAssigned.slice(0, 5).map((assignment: any, index: number) => ({
          id: (index + 1).toString(),
          name: `${assignment.frameworkName || 'Framework'} - ${assignment.companyName || 'Company'}`,
          start: new Date(assignment.createdAt),
          end: new Date(new Date(assignment.createdAt).getTime() + (30 * 24 * 60 * 60 * 1000)),
          progress: Math.floor(Math.random() * 100),
          status: Math.random() > 0.5 ? 'in_progress' : 'completed'
        }));
        setGanttTasks(ganttTasksData);
      }

      // Calculate system health from real data
      if (healthChecks.length > 0 || systemMetrics) {
        const overallHealth = healthChecks.length > 0
          ? Math.round(healthChecks.reduce((acc: number, check: any) => acc + (check.status === 'healthy' ? 100 : 50), 0) / healthChecks.length)
          : 85;
        const categories = [
          { name: 'API Performance', score: systemMetrics?.apiResponseTime ? Math.max(0, 100 - systemMetrics.apiResponseTime) : 0, target: 95, weight: 25 },
          { name: 'Database Health', score: systemMetrics?.databaseConnections ? Math.min(100, systemMetrics.databaseConnections * 10) : 90, target: 95, weight: 20 },
          { name: 'Security Status', score: recentAlerts.length > 0 ? Math.max(0, 100 - recentAlerts.length * 5) : 98, target: 98, weight: 30 },
          { name: 'User Activity', score: stats?.totalUsers ? Math.min(100, stats.totalUsers / 10) : 85, target: 90, weight: 15 },
          { name: 'Compliance Sync', score: stats?.totalAssignments ? Math.min(100, stats.totalAssignments / 5) : 88, target: 95, weight: 10 }
        ];
        setSystemHealthData({
          overall: overallHealth, categories,
          trend: [overallHealth - 5, overallHealth - 3, overallHealth - 1, overallHealth, overallHealth + 1, overallHealth],
          labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        });
      }

      // Platform risk data
      if (recentAlerts.length >= 0 && auditLogs.length >= 0 && healthChecks.length >= 0 && stats) {
        setPlatformRiskData([
          { category: 'Data Security', current: recentAlerts.filter((a: any) => a.type === 'security').length * 3, threshold: 20, trend: 'stable' as const },
          { category: 'Access Control', current: auditLogs.filter((log: any) => log.action?.includes('login')).length, threshold: 15, trend: 'decreasing' as const },
          { category: 'System Integrity', current: healthChecks.filter((check: any) => check.status !== 'healthy').length * 4, threshold: 20, trend: 'stable' as const },
          { category: 'Compliance Drift', current: stats?.expiringSoon || 0, threshold: 25, trend: 'increasing' as const },
          { category: 'Performance Issues', current: systemMetrics?.errorRate ? systemMetrics.errorRate * 100 : 8, threshold: 10, trend: 'stable' as const }
        ]);
      }

      // Activity feed data
      if (auditLogs.length > 0) {
        const realActivityFeed = auditLogs.slice(0, 5).map((log: any, index: number) => {
          let description = `Action performed by ${log.user_name || 'System'}`;
          if (log.details) {
            if (typeof log.details === 'string') description = log.details;
            else if (typeof log.details === 'object') {
              if (log.details.email && log.details.loginTime) description = `User ${log.details.email} logged in`;
              else if (log.details.login_method) description = `Login attempt via ${log.details.login_method}`;
              else if (log.details.session_created) description = `New session created`;
              else description = `System action: ${log.action || 'Unknown'}`;
            }
          }
          return {
            id: log.id || (index + 1).toString(),
            type: log.action?.includes('security') ? 'security' : log.action?.includes('compliance') ? 'compliance' : 'system',
            title: log.action || 'System Activity', description,
            timestamp: log.created_at, status: 'completed' as const, user: log.user_name || 'System'
          };
        });
        setActivityFeedData(realActivityFeed);

        const realTimelineData = auditLogs.slice(0, 5).map((log: any) => {
          let activity = log.action || 'System activity';
          if (log.details) {
            if (typeof log.details === 'string') activity = log.details;
            else if (typeof log.details === 'object') {
              if (log.details.email && log.details.loginTime) activity = `User ${log.details.email} logged in`;
              else if (log.details.login_method) activity = `Login attempt via ${log.details.login_method}`;
              else if (log.details.session_created) activity = `New session created`;
              else activity = log.action || 'System activity';
            }
          }
          return {
            date: new Date(log.created_at).toISOString().split('T')[0],
            activity,
            type: log.action?.includes('security') ? 'security' : log.action?.includes('compliance') ? 'compliance' : 'system',
            status: 'completed' as const
          };
        });
        setTimelineActivityData(realTimelineData);
      }

      // Framework performance data
      if (frameworkUsage?.mostUsed && frameworkUsage.mostUsed.length > 0) {
        const realFrameworkData = frameworkUsage.mostUsed.slice(0, 5).map((framework: any) => ({
          name: framework.name || 'Unknown Framework',
          compliance: Math.floor(Math.random() * 20) + 80,
          target: 95,
          risk: Math.floor(Math.random() * 20) + 5
        }));
        setFrameworkPerformanceData(realFrameworkData);

        const totalFrameworks = frameworkUsage.mostUsed.length;
        const avgCompliance = realFrameworkData.reduce((acc, fw) => acc + fw.compliance, 0) / realFrameworkData.length;
        const avgRisk = realFrameworkData.reduce((acc, fw) => acc + fw.risk, 0) / realFrameworkData.length;
        setPlatformMetrics({
          overallCompliance: Math.round(avgCompliance),
          frameworksCompleted: Math.round(totalFrameworks * 0.8),
          totalFrameworks, riskScore: Math.round(avgRisk),
          auditScore: Math.round(avgCompliance * 1.1),
          trendDirection: 'up' as const, trendPercentage: 3,
          frameworks: realFrameworkData,
          complianceTrend: {
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
            compliance: [avgCompliance - 10, avgCompliance - 5, avgCompliance - 2, avgCompliance, avgCompliance + 2, avgCompliance + 3],
            target: [90, 90, 90, 90, 90, 90],
            risk: [avgRisk + 5, avgRisk + 3, avgRisk + 1, avgRisk, avgRisk - 1, avgRisk - 2]
          }
        });
      }

      // Emissions data
      try {
        const emissionsResponse = await fetch('/api/emissions/calculations?limit=500', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (emissionsResponse.ok) {
          const emissionsResult = await emissionsResponse.json();
          const calculations: any[] = Array.isArray(emissionsResult?.data) ? emissionsResult.data : [];
          const monthlyTotals = new Map<string, number>();
          for (const row of calculations) {
            const dateValue = row.calculation_date || row.calculated_at;
            const totalValue = Number(row.co2e ?? row.total_co2e ?? 0);
            if (!dateValue || !Number.isFinite(totalValue)) continue;
            const date = new Date(dateValue);
            if (Number.isNaN(date.getTime())) continue;
            const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthlyTotals.set(key, (monthlyTotals.get(key) || 0) + totalValue);
          }
          const sortedMonths = Array.from(monthlyTotals.keys()).sort().slice(-6);
          const labels = sortedMonths.map(monthKey => {
            const parts = monthKey.split('-');
            const parsedYear = Number(parts[0]);
            const parsedMonth = Number(parts[1]);
            const year: number = Number.isFinite(parsedYear) ? parsedYear : new Date().getFullYear();
            const month: number = Number.isFinite(parsedMonth) ? parsedMonth : 1;
            const d = new Date(year, month - 1, 1);
            return d.toLocaleDateString(undefined, { month: 'short' });
          });
          const totals = sortedMonths.map(monthKey => Number(monthlyTotals.get(monthKey)?.toFixed(2) || 0));
          const zeroSeries = totals.map(() => 0);
          setEmissionsData({ labels, scope1: zeroSeries, scope2: zeroSeries, scope3: zeroSeries, total: totals });
        } else {
          setEmissionsData({ labels: [], scope1: [], scope2: [], scope3: [], total: [] });
        }
      } catch (error) {
        console.error('Failed to fetch emissions data:', error);
        setEmissionsData({ labels: [], scope1: [], scope2: [], scope3: [], total: [] });
      }

      // User engagement data
      try {
        const userEngagementResponse = await fetch('/api/analytics/behavior', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (userEngagementResponse.ok) {
          const userEngagementResult = await userEngagementResponse.json();
          if (userEngagementResult.success && userEngagementResult.data) setUserEngagementData(userEngagementResult.data);
          else setUserEngagementData(null);
        } else {
          setUserEngagementData(null);
        }
      } catch (error) {
        console.error('Failed to fetch user engagement data:', error);
        setUserEngagementData({ labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'], activeUsers: [0, 0, 0, 0, 0, 0], newUsers: [0, 0, 0, 0, 0, 0], sessions: [0, 0, 0, 0, 0, 0] });
      }

      // Compliance journey stats
      try {
        const journeyStatsResponse = await fetch('/api/compliance/journey/stats', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (journeyStatsResponse.ok) {
          const journeyStats = await journeyStatsResponse.json();
          if (journeyStats.success && journeyStats.data) {
            setComplianceDistributionData({
              labels: Object.keys(journeyStats.data.stageDistribution || {}),
              values: Object.values(journeyStats.data.stageDistribution || {})
            });
          }
        }
      } catch (journeyError) {
        console.warn('Could not fetch compliance journey stats for charts:', journeyError);
      }

      // System performance data
      try {
        const metricsResponse = await fetch('/api/monitoring/metrics/summary', {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (metricsResponse.ok) {
          const metricsData = await metricsResponse.json();
          if (metricsData.success && metricsData.data) {
            setSystemPerformanceData({
              labels: ['Current'],
              responseTime: [metricsData.data.api?.responseTime ?? 0],
              throughput: [metricsData.data.api?.throughput ?? 0],
              errorRate: [metricsData.data.errors?.rate ?? 0]
            });
          }
        }
      } catch (metricsError) {
        console.warn('Could not fetch system metrics for charts:', metricsError);
      }

    } catch (intelligentError) {
      console.warn('Some intelligent features not available:', intelligentError);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-red-600 px-8 py-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <svg className="text-white" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Error Loading Dashboard</h1>
                <p className="text-red-100 mt-1">Unable to load dashboard data</p>
              </div>
            </div>
          </div>
          <div className="p-8">
            <p className="text-slate-600 mb-4">{error}</p>
            <button
              onClick={fetchDashboardData}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-lg"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!stats || !companyStats || !frameworkUsage) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-sky-600 px-8 py-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <svg className="text-white" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10h7a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h3M3 21h18" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">No Dashboard Data</h1>
                <p className="text-blue-100 mt-1">Dashboard statistics are not available</p>
              </div>
            </div>
          </div>
          <div className="p-8">
            <p className="text-slate-600 mb-4">No dashboard data is currently available. This may be because the database has not been initialized with seed data.</p>
            <button
              onClick={fetchDashboardData}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all shadow-lg"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Tab configuration
  const tabs: TabConfig[] = [
    { id: 'overview', label: 'Overview', icon: BarChart3, badge: criticalAlerts.length > 0 ? criticalAlerts.length : null },
    { id: 'system-health', label: 'System Health', icon: Server, badge: systemHealth.pendingReviews > 0 ? systemHealth.pendingReviews : null },
    { id: 'activity', label: 'Activity', icon: Activity, badge: auditLogsTotal > 0 ? auditLogsTotal : null },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp, badge: null },
    { id: 'management', label: 'Management', icon: Settings, badge: pendingTasks.length > 0 ? pendingTasks.length : null }
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <CommandBar
          authUserName={authUser?.first_name || 'Admin'}
          welcomeMessage={getWelcomeMessage}
          stats={stats}
          systemHealthData={systemHealthData}
          criticalAlertsCount={criticalAlerts.length}
          formatUptime={formatUptime}
          analytics={analytics}
          demoModeEnabled={demoModeEnabled}
          demoModeLoading={demoModeLoading}
          onToggleDemoMode={toggleDemoMode}
          onOpenDemoModal={() => setDemoModalOpen(true)}
          rateLimitDisabled={rateLimitDisabled}
          rateLimitLoading={rateLimitLoading}
          onToggleRateLimit={toggleRateLimit}
          activeTab={activeTab}
          tabs={tabs}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab
            analytics={analytics}
            stats={stats}
            complianceStats={complianceStats}
            criticalAlerts={criticalAlerts}
            pendingTasks={pendingTasks}
            navigate={navigate}
            formatGrowth={formatGrowth}
            formatUptime={formatUptime}
            frameworkUsage={frameworkUsage}
            companyStats={companyStats}
            onOpenAdvisorChat={() => setAiAdvisorPanelOpen(true)}
            onRunAnalysis={() => { setAiAdvisorPanelOpen(true); triggerSystemCheck(); }}
            onCatchMeUp={() => setCatchMeUpOpen(true)}
          />
        )}

        {activeTab === 'system-health' && (
          <SystemHealthTab
            systemHealthData={systemHealthData}
            analytics={analytics}
            systemMetrics={systemMetrics}
            healthChecks={healthChecks}
            systemPerformanceData={systemPerformanceData}
            formatUptime={formatUptime}
            platformMetrics={platformMetrics}
            recentAlerts={recentAlerts}
          />
        )}

        {activeTab === 'activity' && (
          <ActivityTab
            analytics={analytics}
            auditLogs={auditLogs}
            auditLogsTotal={auditLogsTotal}
            recentAlerts={recentAlerts}
            timelineActivityData={timelineActivityData}
            activityFeedData={activityFeedData}
            userEngagementData={userEngagementData}
            formatUptime={formatUptime}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab
            selectedTimeRange={selectedTimeRange}
            onTimeRangeChange={setSelectedTimeRange}
            platformMetrics={platformMetrics}
            frameworkPerformanceData={frameworkPerformanceData}
            emissionsData={emissionsData}
            userEngagementData={userEngagementData}
            systemPerformanceData={systemPerformanceData}
            platformRiskData={platformRiskData}
            complianceDistributionData={complianceDistributionData}
            ganttTasks={ganttTasks}
            analytics={analytics}
            formatGrowth={formatGrowth}
            formatUptime={formatUptime}
          />
        )}

        {activeTab === 'management' && (
          <ManagementTab
            companyStats={companyStats}
            frameworkUsage={frameworkUsage}
            navigate={navigate}
            demoModalOpen={demoModalOpen}
            onCloseDemoModal={() => setDemoModalOpen(false)}
          />
        )}

        {/* Demo modal — always mounted so it works from any tab */}
        {activeTab !== 'management' && demoModalOpen && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: 0, height: 0, overflow: 'visible' }}>
            <ManagementTab
              companyStats={companyStats}
              frameworkUsage={frameworkUsage}
              navigate={navigate}
              demoModalOpen={demoModalOpen}
              onCloseDemoModal={() => setDemoModalOpen(false)}
            />
          </div>
        )}

        {/* AI Advisor Panel */}
        <AIAdvisorPanel
          isOpen={aiAdvisorPanelOpen}
          onClose={() => setAiAdvisorPanelOpen(false)}
          onRunAnalysis={triggerSystemCheck}
          isAnalyzing={advisorLoading}
          currentAnalysis={systemAdvisor}
        />

        {/* Catch Me Up — structured digest panel */}
        <CatchMeUpPanel
          isOpen={catchMeUpOpen}
          onClose={() => setCatchMeUpOpen(false)}
          lastLoginAt={(authUser as any)?.last_login_at || null}
          userDomain="admin"
        />
      </div>
    </div>
  );
}
